import React, { useEffect, useReducer, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingScreen from "../../../components/LoadingScreen";
import ToastHot from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import Reducer from "../../../services/Reducer";
import api from "../../../services/Axios";

const initState = {
  searchTerm: "",
  FaqList: [],
  totalItem: 0,
  loading: false,
};

export default function Faq() {
  const [state, dispatch] = useReducer(Reducer, initState);

  const [page, setPage] = useState(1);

  // Modal state (simple custom modal using your existing classes)
  const [openModal, setOpenModal] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [selectedFaq, setSelectedFaq] = useState(null);

  const [form, setForm] = useState({ title: "", content: "" });
  const [formErrors, setFormErrors] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);

  const getAuthHeader = () => ({
    baseURL:" https://dev-dj-music-api.teexponent.com/api/",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  // -----------------------------
  // API Calls
  // -----------------------------
  const GetAllFaqs = async () => {
    const header = getAuthHeader();
    return api
      .get(`/faq/get-all`, header)
      .then((res) => res)
      .catch((err) => err?.response);
  };

  const CreateFaq = async (payload) => {
    const header = getAuthHeader();
    return api
      .post(`/faq/create`, payload, header)
      .then((res) => res)
      .catch((err) => err?.response);
  };

  const UpdateFaq = async (id, payload) => {
    const header = getAuthHeader();
    return api
      .patch(`/faq/update/${id}`, payload, header)
      .then((res) => res)
      .catch((err) => err?.response);
  };

  const DeleteFaq = async (id) => {
    const header = getAuthHeader();
    return api
      .delete(`/faq/delete/${id}`, header)
      .then((res) => res)
      .catch((err) => err?.response);
  };

  // -----------------------------
  // Load list
  // -----------------------------
  const loadFaqs = async () => {
    dispatch({ type: "LOAD", payload: true });

    const response = await GetAllFaqs();

    dispatch({ type: "LOAD", payload: false });

    if (response?.data?.success) {
      // Most common shapes:
      // { success: true, data: [...] }
      // { success: true, data: { data: [...] } }
      const raw = response?.data?.data;
      const list = Array.isArray(raw) ? raw : raw?.data || [];

      dispatch({
        type: "SETDATA",
        payload: { name: "FaqList", value: list },
      });
      dispatch({
        type: "SETDATA",
        payload: { name: "totalItem", value: list.length },
      });
    } else {
      dispatch({
        type: "SETDATA",
        payload: { name: "FaqList", value: [] },
      });
      dispatch({
        type: "SETDATA",
        payload: { name: "totalItem", value: 0 },
      });
      ToastHot.error(response?.data?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    loadFaqs();
  }, []);

  // -----------------------------
  // Search (debounced)
  // -----------------------------
  const debounce = (func) => {
    let timer;
    return function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        func.apply(this, args);
      }, 500);
    };
  };

  const handleChangeSearch = (e) => {
    const { value } = e.target;
    dispatch({
      type: "SETDATA",
      payload: { name: "searchTerm", value: decodeURI(value) },
    });
    setPage(1);
  };

  const optimizedVersion = React.useCallback(debounce(handleChangeSearch), []);

  // -----------------------------
  // Helpers
  // -----------------------------
  const getIsActive = (faq) => {
    // supports: status: "A"/"I" OR isActive boolean
    if (faq?.status) return faq.status === "A";
    if (typeof faq?.isActive === "boolean") return faq.isActive;
    return true; // default
  };

  const getActiveLabel = (faq) => (getIsActive(faq) ? "Active" : "Inactive");

  const validateForm = () => {
    const errs = { title: "", content: "" };
    if (!form.title.trim()) errs.title = "Title is required";
    if (!form.content.trim()) errs.content = "Content is required";

    setFormErrors(errs);
    return !errs.title && !errs.content;
  };

  // -----------------------------
  // Create / Edit Modal handlers
  // -----------------------------
  const openCreate = () => {
    setMode("create");
    setSelectedFaq(null);
    setForm({ title: "", content: "" });
    setFormErrors({ title: "", content: "" });
    setOpenModal(true);
  };

  const openEdit = (faq) => {
    setMode("edit");
    setSelectedFaq(faq);
    setForm({
      title: faq?.title ? String(faq.title) : "",
      content: faq?.content ? String(faq.content) : "",
    });
    setFormErrors({ title: "", content: "" });
    setOpenModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setOpenModal(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const payload = {
      title: form.title.trim(),
      content: form.content.trim(),
    };

    let response;
    if (mode === "create") {
      response = await CreateFaq(payload);
    } else {
      response = await UpdateFaq(selectedFaq?.id, payload);
    }

    setSaving(false);

    if (response?.data?.success || response?.status === 200) {
      ToastHot.success(mode === "create" ? "FAQ created" : "FAQ updated");
      setOpenModal(false);
      loadFaqs();
    } else {
      ToastHot.error(response?.data?.message || "Something went wrong");
    }
  };

  // -----------------------------
  // Actions: Delete / Toggle Active
  // -----------------------------
  const popUpForConfirmation = (faq, type) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="custom-ui-alert-popup default-alert-popup">
            <div className="alert-popup-headerpart">
              <h1>Are you sure?</h1>
              <div className="alert-popup-close" onClick={onClose}>
                X
              </div>
            </div>

            <div className="alert-popup-bodypart">
              {type === "delete" ? (
                <p>You want to delete this FAQ?</p>
              ) : (
                <p>You want to {getIsActive(faq) ? "Inactive" : "Active"} this FAQ?</p>
              )}
            </div>

            <div className="alert-actions">
              <div className="alert-actions-col">
                <button onClick={onClose} className="alert--cancel-btn">
                  No
                </button>
              </div>
              <div className="alert-actions-col">
                <button
                  className="alert--submit-btn"
                  onClick={async () => {
                    if (type === "delete") {
                      await handleDeleteFaq(faq);
                    } else {
                      await handleToggleActive(faq);
                    }
                    onClose();
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        );
      },
    });
  };

  const handleDeleteFaq = async (faq) => {
    const response = await DeleteFaq(faq?.id);
    if (response?.data?.success || response?.status === 200) {
      ToastHot.success("FAQ deleted successfully.");
      loadFaqs();
    } else {
      ToastHot.error(response?.data?.message || "Something went wrong");
    }
  };

  const handleToggleActive = async (faq) => {
    // Not provided in payload, but your UI needs "active".
    // We'll send status/isActive too; backend can ignore extra fields if not supported.
    const nextActive = !getIsActive(faq);

    const payload = {
      title: faq?.title ?? "",
      content: faq?.content ?? "",
      status: nextActive ? "A" : "I",
      isActive: nextActive,
    };

    const response = await UpdateFaq(faq?.id, payload);

    if (response?.data?.success || response?.status === 200) {
      ToastHot.success(`FAQ marked as ${nextActive ? "Active" : "Inactive"}`);
      loadFaqs();
    } else {
      ToastHot.error(response?.data?.message || "Something went wrong");
    }
  };

  // -----------------------------
  // Filter + Pagination (client-side)
  // -----------------------------
  const pageSize = 10;
  const term = (state.searchTerm || "").toLowerCase().trim();

  const filtered = (state.FaqList || []).filter((faq) => {
    if (!term) return true;
    const t = (faq?.title || "").toLowerCase();
    const c = (faq?.content || "").toLowerCase();
    return t.includes(term) || c.includes(term);
  });

  const totalItem = filtered.length;
  const pageCount = totalItem === 0 ? 1 : Math.ceil(totalItem / pageSize);

  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const paginated = filtered.slice(start, end);

  const handleChangePage = (event, value) => setPage(value);

  return (
    <>
      <Helmet>
        <title>FAQ</title>
      </Helmet>

      <div className="listingPages allAccountsPage">
        <div className="listingPagesHead">
          <div className="left">
            <h3>FAQ</h3>
          </div>

          <div className="right">
            <div className="rightInner">
              <div className="searchGroup">
                <div>
                  <i className="material-icons">search</i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    id="searchTerm"
                    name="searchTerm"
                    onChange={optimizedVersion}
                  />
                </div>
              </div>

              {/* Same style button */}
              <a className="themeBtn" style={{ cursor: "pointer" }} onClick={openCreate}>
                <i className="material-icons">add</i>Add New
              </a>
            </div>
          </div>
        </div>

        <div className="listingMain">
          {state.loading && <LoadingScreen />}

          <div className="customTable allaccountsTable table-responsive scrollbox">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Content</th>
                  {/* <th>Status</th> */}
                  <th className="text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginated && paginated.length > 0 ? (
                  paginated.map((faq, index) => (
                    <tr key={faq?.id || index}>
                      <td className="w250px prewrap-text" data-th="Title">
                        {faq?.title || "-"}
                      </td>

                      <td className="w320px prewrap-text" data-th="Content">
                        {faq?.content || "-"}
                      </td>

                      {/* <td className="w120px prewrap-text" data-th="Status">
                        {getActiveLabel(faq)}
                      </td> */}

                      <td className="actionTd w60px" data-th="Action">
                        <div className="dropdown">
                          <button
                            className="btn dropdown-toggle"
                            type="button"
                            id={`dropdownMenuButton_${faq?.id || index}`}
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="material-icons">more_vert</i>
                          </button>

                          <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton_${faq?.id || index}`}>
                            {/* <li>
                              <a
                                style={{ cursor: "pointer" }}
                                className="dropdown-item"
                                onClick={() => popUpForConfirmation(faq, "toggle")}
                              >
                                {getIsActive(faq) ? "Inactive" : "Active"}
                              </a>
                            </li> */}

                            <li>
                              <a
                                style={{ cursor: "pointer" }}
                                className="dropdown-item"
                                onClick={() => openEdit(faq)}
                              >
                                Edit
                              </a>
                            </li>

                            <li>
                              <a
                                style={{ cursor: "pointer" }}
                                className="dropdown-item"
                                onClick={() => popUpForConfirmation(faq, "delete")}
                              >
                                Delete
                              </a>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <div className="nodata-found-table">
                        <img src="https://img.freepik.com/premium-vector/no-data-found-empty-file-folder-concept-design-vector-illustration_620585-1698.jpg" />
                        <h3>no FAQ is still added!</h3>
                        <div className="nodata-found-actions">
                          <a className="themeBtn" style={{ cursor: "pointer" }} onClick={openCreate}>
                            Add FAQ
                          </a>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filtered && filtered.length > 0 && (
            <div className="tablePagination">
              <div className="left">
                <p>
                  Results {page !== 1 ? page * 10 - 9 : page} -{" "}
                  {totalItem > page * 10 ? page * 10 : totalItem} of {totalItem}
                </p>
              </div>
              <div className="right">
                <div className="paginationNavigation">
                  <nav aria-label="Page navigation example">
                    <Stack spacing={2}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handleChangePage}
                        variant="outlined"
                        color="primary"
                      />
                    </Stack>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* -------------------------
          Create/Edit Modal
          ------------------------- */}
{openModal && (
  <>
    {/* ✅ Modal (higher z-index) */}
    <div
      className="modal fade show"
      style={{ display: "block", zIndex: 1060, pointerEvents: "auto" }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ pointerEvents: "auto" }}>
        <div
          className="modal-content"
          style={{ background: "#0E243F", color: "#fff", pointerEvents: "auto" }}
        >
          <div className="modal-header">
            <h5 className="modal-title">{mode === "create" ? "Add FAQ" : "Edit FAQ"}</h5>
            <button type="button" className="btn-close" onClick={closeModal}></button>
          </div>

          <div className="modal-body">
            <div className="formGroup">
              <label className="text-white mb-2">Title</label>
              <div className="inputgroup">
                <input
                  type="text"
                  className="form-control form-control-v1"
                  value={form.title}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, title: e.target.value }));
                    setFormErrors((p) => ({ ...p, title: "" }));
                  }}
                />
              </div>
              {formErrors.title && <small style={{ color: "red" }}>{formErrors.title}</small>}
            </div>

            <div className="formGroup">
              <label className="text-white mb-2">Content</label>
              <div className="inputgroup">
                <textarea
                  className="form-control form-control-v1"
                  rows={5}
                  value={form.content}
                  onChange={(e) => {
                    setForm((p) => ({ ...p, content: e.target.value }));
                    setFormErrors((p) => ({ ...p, content: "" }));
                  }}
                />
              </div>
              {formErrors.content && <small style={{ color: "red" }}>{formErrors.content}</small>}
            </div>
          </div>

          <div className="modal-footer">
            <button className="themeOutlineBtn" onClick={closeModal} disabled={saving}>
              Cancel
            </button>
            <button className="themeBtn" onClick={handleSubmit} disabled={saving}>
              {saving ? "Loading..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* ✅ Backdrop (lower z-index) */}
    <div
      className="modal-backdrop fade show"
      style={{ zIndex: 1050 }}
      onClick={closeModal}   // optional: click outside closes
    />
  </>
)}
    </>
  );
}
